import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Calendar, Mail, MapPin, Phone } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../lib/api";

// --- Cal.com booking link ---
// To use your own Cal.com account, sign up at https://cal.com (free),
// create an event type (e.g. "talent-strategy-session"),
// and replace CAL_LINK below with "<your-username>/<event-slug>".
const CAL_LINK = "rick"; // public demo profile — swap with your own Cal.com link

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company_name: "",
    urgency: "Flexible",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Cal.com official inline embed snippet
    (function (C, A, L) {
      let p = function (a, ar) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal; let ar = arguments;
        if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
        if (ar[0] === L) { const api = function () { p(api, arguments); }; const namespace = ar[1]; api.q = api.q || []; if (typeof namespace === "string") { cal.ns[namespace] = cal.ns[namespace] || api; p(cal.ns[namespace], ar); p(cal, ["initNamespace", namespace]); } else p(cal, ar); return; }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    try {
      window.Cal("init", { origin: "https://cal.com" });
      window.Cal("inline", {
        elementOrSelector: "#cal-embed",
        calLink: CAL_LINK,
        layout: "month_view",
        config: { theme: "light" },
      });
      window.Cal("ui", {
        styles: { branding: { brandColor: "#10B981" } },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
    } catch (err) {
      console.error("Cal.com embed error", err);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ full_name: "", email: "", company_name: "", urgency: "Flexible", message: "" });
    } catch {
      toast.error("Unable to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative pt-36 pb-20 bg-navy text-white overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 hero-grid opacity-25" />
        <div className="absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full bg-[#10B981]/10 blur-[120px]" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-[#10B981]" />
            <span className="eyebrow text-[#10B981]">Contact</span>
            <span className="h-px w-10 bg-[#10B981]" />
          </div>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
            Book a <em className="text-[#10B981] not-italic">Discovery Call</em>.
          </h1>
          <p className="mt-6 text-white/75 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            Two-click calendar booking for employers. Direct inbox for candidates and
            partners. Real humans, one business day response.
          </p>
        </div>
      </section>

      {/* CAL.COM + CONTACTS */}
      <section className="py-24 bg-ivory" data-testid="contact-calendly-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="eyebrow text-[#10B981]">Book Direct</div>
            <h2 className="mt-4 font-serif text-4xl text-navy leading-tight">
              Talent Strategy Session · 30 minutes
            </h2>
            <p className="mt-4 text-navy/65 leading-relaxed max-w-xl">
              Walk us through your open role. Walk out with a role scorecard, a salary
              benchmark, and a realistic shortlist timeline.
            </p>

            <div className="mt-8 rounded-2xl overflow-hidden ring-1 ring-navy/10 bg-white shadow-xl" data-testid="cal-embed-wrapper">
              <div id="cal-embed" style={{ minHeight: 720, width: "100%" }} />
            </div>
            <p className="mt-3 text-xs text-navy/45">
              Powered by <a href="https://cal.com" target="_blank" rel="noreferrer" className="text-[#10B981] hover:underline">Cal.com</a> · Replace the booking link in <code className="font-mono text-[10px] bg-navy/5 px-1.5 py-0.5 rounded">Contact.jsx</code> with your own Cal.com username.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            <div className="card-lux" data-testid="contact-card-direct">
              <Calendar className="h-5 w-5 text-[#10B981]" />
              <div className="mt-4 eyebrow text-navy/50">Prefer to write?</div>
              <h3 className="mt-2 font-serif text-2xl text-navy">Drop us a line</h3>
              <p className="mt-3 text-sm text-navy/65 leading-relaxed">
                We reply to every inbound within one business day — candidate or
                employer.
              </p>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex items-center gap-3 text-navy/80">
                  <Mail className="h-4 w-4 text-[#10B981]" />
                  <span>careers@directhirepayroll.com</span>
                </div>
                <div className="flex items-center gap-3 text-navy/80">
                  <Phone className="h-4 w-4 text-[#10B981]" />
                  <span>+1 (212) 555-0136</span>
                </div>
                <div className="flex items-center gap-3 text-navy/80">
                  <MapPin className="h-4 w-4 text-[#10B981]" />
                  <span>55 Water St, Financial District, NYC</span>
                </div>
              </div>
            </div>

            <div className="card-lux bg-navy text-white border-white/5" data-testid="contact-card-hours">
              <div className="eyebrow text-[#D4AF37]">Hours</div>
              <div className="mt-4 font-serif text-xl">Mon – Fri · 8:30a – 6p ET</div>
              <div className="mt-2 text-white/60 text-sm">Urgent hires? Note "urgent" in the form and we'll escalate.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="py-28 bg-white" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <div className="eyebrow text-[#10B981]">Contact Form</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-tight">
              Send us a note.
            </h2>
            <p className="mt-5 text-navy/65 max-w-2xl mx-auto text-lg leading-relaxed">
              Include your company name and urgency — we'll triage accordingly.
            </p>
          </div>

          <form onSubmit={submit} className="card-lux p-10 mt-12 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="ctname">Full Name *</Label>
                <Input id="ctname" data-testid="contact-name" className="mt-2" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Kim" />
              </div>
              <div>
                <Label htmlFor="ctemail">Email *</Label>
                <Input id="ctemail" type="email" data-testid="contact-email" className="mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@acme.com" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="ctcompany">Company Name</Label>
                <Input id="ctcompany" data-testid="contact-company" className="mt-2" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Finance, Inc." />
              </div>
              <div>
                <Label>Urgency of Hire</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger className="mt-2" data-testid="contact-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Immediate">Immediate (2 weeks)</SelectItem>
                    <SelectItem value="30 days">Within 30 days</SelectItem>
                    <SelectItem value="60 days">Within 60 days</SelectItem>
                    <SelectItem value="Flexible">Flexible / exploratory</SelectItem>
                    <SelectItem value="I'm a candidate">I'm a candidate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="ctmsg">Message *</Label>
              <Textarea id="ctmsg" rows={5} className="mt-2" data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about the role, the stack, or your background…" />
            </div>

            <button disabled={submitting} type="submit" className="btn-emerald w-full disabled:opacity-70" data-testid="contact-submit-btn">
              {submitting ? "Sending…" : "Send Message"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
            <p className="text-xs text-navy/45 text-center">We respond within 1 business day.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
